import React, { useContext, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { AccountHeaderSlotsContext } from './accountHeaderSlots';
import ArchivedToggle from '../ui/ArchivedToggle';
import ExportButton from '../header/ExportButton';
import type { ExporterKey } from '../header/exporters';
import { accountCrumbs, type Crumb } from '../../utils/accountCrumbs';
import type { AccountView } from '../../utils/accountRoutes';
import '../../styles/components/AccountPage.css';

export type { Crumb };

interface AccountBarProps {
  /** Which account screen this is. The trail is derived from it (utils/accountCrumbs.ts); a page never writes its own. */
  view: AccountView;
  /** The page's own name when it varies: the month on the Calendar, a record's number or name on a form. */
  leaf?: string;
  /** Crumbs between the parent list and the page (the invoice a payment belongs to). */
  via?: readonly Crumb[];
  /** Buttons and toggles that belong to this page (New, filters, Save/Cancel, Archived...). */
  actions?: React.ReactNode;
  /** The Archived switch, first in the bar as on the project lists. */
  archived?: { checked: boolean; onChange: (checked: boolean) => void };
  /** The icon-only Export button, last in the bar as on the project lists. Its exporter is registered with useExport. */
  exportKey?: ExporterKey;
}

/**
 * What an account-level page puts in the app header bar: where you are on the
 * left, what you can do on the right. Inside AccountShell the content is
 * portalled into the shell's header bar, the same bar the per-project screens
 * get from Header, so the page body never repeats a title, a button or a filter.
 * Every page has a trail (Workspace / ... / this page); it truncates before the
 * actions on the right ever wrap to a second row.
 *
 * Rendered outside the shell (a page mounted on its own, as the tests do) it
 * falls back to an in-page strip so the same controls are still reachable.
 */
export const AccountBar: React.FC<AccountBarProps> = ({ view, leaf, via, actions, archived, exportKey }) => {
  const slots = useContext(AccountHeaderSlotsContext);
  const registerBar = slots?.registerBar;
  useLayoutEffect(() => (registerBar ? registerBar() : undefined), [registerBar]);

  const crumbs = useMemo(() => accountCrumbs(view, leaf, via), [view, leaf, via]);

  const trail = (inShell: boolean) => (
    <nav className={inShell ? 'global-breadcrumb-trail' : 'ap-crumbs'} aria-label="Breadcrumb">
      {crumbs.map((crumb, i) => {
        const last = i === crumbs.length - 1;
        const sep = inShell ? 'global-breadcrumb-separator' : 'ap-crumb-sep';
        return (
          <React.Fragment key={`${crumb.label}-${i}`}>
            {i > 0 && <span className={sep} aria-hidden="true">/</span>}
            {crumb.to && !last ? (
              <Link className={inShell ? 'global-breadcrumb-link' : 'ap-crumb-link'} to={crumb.to} title={crumb.label}>{crumb.label}</Link>
            ) : (
              <span className={inShell ? 'global-breadcrumb-current' : 'ap-crumb-current'} aria-current={last ? 'page' : undefined} title={crumb.label}>{crumb.label}</span>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );

  // Same order as the project bars: Archived, the page's filters and New, then Export.
  const content = archived || actions || exportKey ? (
    <>
      {archived && <ArchivedToggle checked={archived.checked} onChange={archived.onChange} />}
      {actions}
      {exportKey && <ExportButton exportKey={exportKey} />}
    </>
  ) : null;

  if (slots) {
    return (
      <>
        {slots.crumbs && createPortal(trail(true), slots.crumbs)}
        {slots.actions && content && createPortal(content, slots.actions)}
      </>
    );
  }

  return (
    <div className="ap-bar">
      {trail(false)}
      {content && <div className="ap-actions">{content}</div>}
    </div>
  );
};

export default AccountBar;
